import React, { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Button, Paper, Typography, CircularProgress, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Menu, MenuItem } from '@mui/material';
import { ApplicationFormModal } from '../../../components/Modals/ApplicationFormModal';
import { useAuth } from '../../../contexts/AuthContext';
import { useSchools } from '../../../hooks/useSchools';
import { useTeacher } from '../../../hooks/useTeacher';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSessionClass } from 'contexts/SessionClassContext';
import { useTranslation } from 'react-i18next';
import EnhancedTable from '../../../components/common/EnhancedTable';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTenantHeader } from '../../../hooks/useTenantHeader';
import { useError } from '../../../contexts/ErrorContext';
import StudentHistoryModal from '../../../components/Modals/StudentHistoryModal';
import StudentDataSheetSortModal from '../../../components/Modals/StudentDataSheetSortModal';
import ScholarNumberModal from '../../../components/Modals/ScholarNumberModal';
import { useStudents, ApplicationStudentDTO, StudentRollScholarUpdateDto } from '../../../hooks/useStudents';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AddIcon from '@mui/icons-material/Add';
import { ApplicationFormData } from '../../../components/Modals/ApplicationFormModal';
import { format } from 'date-fns';
import { useMedium } from '../../../components/layout/Layout';
import { useMarkSheets } from '../../../hooks/useMarkSheets';
import Tooltip from '@mui/material/Tooltip';
import { useClasses } from '../../../hooks/useClasses';

const StudentManagement: React.FC = () => {
  const { selectedSession, selectedClass } = useSessionClass();
  const { t } = useTranslation();
  const { tenantLoading } = useTenantHeader();
  const { user, isSchoolAdmin, isTeacher } = useAuth();
  const { showError, showSuccess } = useError();
  const schoolId = user?.schoolId;
  const { useSchoolById } = useSchools();
  const { classesQuery } = useClasses(schoolId || undefined);
  const { teachersQuery } = useTeacher(undefined, tenantLoading);
  const { data: school } = useSchoolById(schoolId || undefined);
  const { selectedMedium } = useMedium();

  const {
    updateStudentWithDTOMutation,
    deleteStudentMutation,
    downloadStudentsMutation,
    updateStudentUserStatusMutation,
    updateRollNumberAndScholarNumberMutation,
    useStudentsBySessionIdAndClassId,
    useStudentsBySessionIdAndClassName,
  } = useStudents(tenantLoading, schoolId || undefined);
  
  const classIdFilter =
    typeof selectedClass?.id === "number" && selectedClass.id > 0
      ? selectedClass.id
      : undefined;

  const classNameFilter =
    selectedClass?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : selectedClass?.section === "__GRADE_ALL__"
        ? `${String(selectedClass?.name || "").trim()}-ALL`
        : (String(selectedClass?.name || "").includes("-ALL") ? String(selectedClass?.name || "") : undefined);

  const marksheetsClassIdParam = classNameFilter ? undefined : classIdFilter;
  const marksheetsClassNameParam = classNameFilter ? classNameFilter : undefined;

  // Fetch students by sessionId + (classId OR className for *-ALL / ALL)
  const studentsBySessionIdAndClassIdQuery = useStudentsBySessionIdAndClassId(
    selectedSession?.id,
    classIdFilter
  );

  const studentsBySessionIdAndClassNameQuery = useStudentsBySessionIdAndClassName(
    selectedSession?.id,
    classNameFilter
  );

  const studentsQuery =
    classNameFilter ? studentsBySessionIdAndClassNameQuery : studentsBySessionIdAndClassIdQuery;
  
  // Use the new query's loading and error states
  const isLoading = studentsQuery.isLoading;
  const error = studentsQuery.error;
  const refetch = studentsQuery.refetch;
  const { marksheetsQuery } = useMarkSheets(
    tenantLoading,
    false,
    marksheetsClassIdParam,
    selectedSession?.id,
    undefined,
    undefined,
    false,
    marksheetsClassNameParam
  );
  const queryClient = useQueryClient();

  // Clear students cache when schoolId changes to prevent cross-school data
  useEffect(() => {
    if (schoolId) {
      // Invalidate students queries when schoolId changes
      queryClient.invalidateQueries({
        queryKey: ["students", schoolId],
      });
      // Also invalidate studentClassMapping queries
      queryClient.invalidateQueries({
        queryKey: ["studentClassMapping"],
      });
    }
  }, [schoolId, queryClient]);


  const [modalOpen, setModalOpen] = useState(false);
  const [sortModalOpen, setSortModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentStudent, setCurrentStudent] = useState<any>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<any>(null);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyStudentId, setHistoryStudentId] = useState<number | null>(null);
  const [historyStudentName, setHistoryStudentName] = useState<string>('');
  const [historyStudentRollNo, setHistoryStudentRollNo] = useState<string>('');
  const [scholarNumberModalOpen, setScholarNumberModalOpen] = useState(false);
  const [profileStatusDialogOpen, setProfileStatusDialogOpen] = useState(false);
  const [studentToUpdateStatus, setStudentToUpdateStatus] = useState<{ id: number; userStatus: boolean } | null>(null);

  // Helper function to ensure dates are in YYYY-MM-DD format (as expected by student API)
  const ensureValidDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
    
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        return dateString;
      }
      
      // If it's in dd/MM/yyyy format, convert to YYYY-MM-DD
      if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        const [day, month, year] = dateString.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      
      // Try to parse as Date and format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      // If all parsing fails, return today's date as fallback
      const today = new Date();
      return today.toISOString().split('T')[0];
    } catch (error) {
      const today = new Date();
      return today.toISOString().split('T')[0];
    }
  };

  // Helper function to convert student data to ApplicationFormData format
  const convertStudentToApplicationForm = (student: any): ApplicationFormData => {

    // Helper function to convert various date formats to dd/MM/yyyy format
    const convertDateToDisplayFormat = (dateInput: string | number[] | null | undefined): string => {
      if (!dateInput) return '';
      
      try {
        // Handle array format dates [year, month, day] - common from APIs
        if (Array.isArray(dateInput) && dateInput.length >= 3) {
          const [year, month, day] = dateInput;
          if (typeof year === 'number' && typeof month === 'number' && typeof day === 'number') {
            // Note: Backend sends month as 1-indexed (1=Jan, 12=Dec), so we use it directly
            const date = new Date(year, month - 1, day);
            if (!isNaN(date.getTime())) {
              return format(date, 'dd/MM/yyyy');
            }
          }
        }
        
        // Handle string format dates
        if (typeof dateInput === 'string') {
          // If it's already in dd/MM/yyyy format, return as is
          if (dateInput.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
            return dateInput;
          }
          
          // If it's in YYYY-MM-DD format, convert to dd/MM/yyyy
          if (dateInput.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [year, month, day] = dateInput.split('-');
            return `${day}/${month}/${year}`;
          }
          
          // Try to parse as Date and format
          const date = new Date(dateInput);
          if (!isNaN(date.getTime())) {
            return format(date, 'dd/MM/yyyy');
          }
        }
        
        // If all parsing fails, return empty string (no fallback to today's date)
        return '';
      } catch (error) {
        return '';
      }
    };

    // Helper function to parse subjects from comma-separated string or array
    const parseSubjects = (subjectsData: any): string[] => {
      if (!subjectsData) return [];
      
      try {
        // If it's already an array, use it directly
        if (Array.isArray(subjectsData)) {
          return subjectsData.filter(Boolean);
        }
        
        // If it's a string, split by comma
        if (typeof subjectsData === 'string') {
          return subjectsData.split(',').map(s => s.trim()).filter(Boolean);
        }
        
        // If it's individual subject fields, extract codes
        if (subjectsData.subject1 || subjectsData.subject2 || subjectsData.subject3 || 
            subjectsData.subject4 || subjectsData.subject5 || subjectsData.subject6) {
          const subjects = [
            extractSubjectCode(subjectsData.subject1),
            extractSubjectCode(subjectsData.subject2),
            extractSubjectCode(subjectsData.subject3),
            extractSubjectCode(subjectsData.subject4),
            extractSubjectCode(subjectsData.subject5),
            extractSubjectCode(subjectsData.subject6)
          ].filter(Boolean); // Remove empty strings
          return subjects;
        }
        
        return [];
      } catch (error) {
        return [];
      }
    };

    // Helper function to extract subject code from full subject name
    // Backend sends "(411) English" but form expects "411"
    const extractSubjectCode = (fullSubjectName: string | null | undefined): string => {
      if (!fullSubjectName) return '';
      const codeMatch = fullSubjectName.match(/\((\d+)\)/);
      return codeMatch ? codeMatch[1] : fullSubjectName;
    };

    // Parse subjects from the student data
    const subjects = parseSubjects(student.subjects || student);

    return {
      id: student.id,
      applicationNo: student.applicationNo || '',
      rollNo: student.rollNumber || '',
      enrolmentNo: student.enrolmentNo || '',
      scholarNo: student.scholarNo || '',
      sssmid: student.sssmid || '',
      penNo: student.penNo || '',
      apparId: student.apparId || '', // Fixed: was using student.apparIdNo
      studentCode: student.studentCode || '', // Include studentCode field
      studentName: student.studentName || `${student.firstName || ''} ${student.lastName || ''}`.trim() || 'Unknown Student',
      fatherName: student.fatherName || '',
      motherName: student.motherName || '',
      dateOfBirth: convertDateToDisplayFormat(student.dateOfBirth),
      gender: student.gender || '',
      religion: student.religion || '',
      caste: student.caste || '',
      subCaste: student.subCaste || '',
      casteCertificate: student.casteCertificate || '',
      aadharNo: student.aadharNo || '',
      houseNo: student.houseNo || '',
      street: student.street || '',
      landmark: student.landmark || '',
      area: student.area || '',
      city: student.village || student.city || '',
      postOffice: student.postOffice || '',
      district: student.district || '',
      state: student.state || '',
      pinCode: student.pinCode || '',
      mobileNo: student.mobileNo || student.phone || '', // Fixed: was using student.phone, added fallback
      gmail: student.gmail || student.email || '',
      bankName: student.bankName || '',
      ifcCode: student.ifcCode || '',
      accountNo: student.accountNo || '',
      annualIncome: student.annualIncome || '',
      admissionDate: convertDateToDisplayFormat(student.admissionDate),
      admissionClass: student.admissionClass || student.className || '',
      section: student.section || '',
      medium: student.medium || '',
      admissionType: student.admissionType || '',
      stream: student.stream || '', // Stream field with default empty string
      subjects: subjects, // Use the new subjects array
      schoolId: student.schoolId || schoolId || 0,
      isRegistered: 'Approved', // Since this is an existing student (approved)
      sessionId: student.sessionId || selectedSession?.id || 0,
      sessionName: selectedSession?.name || '',
      cwsnCode: student.cwsnCode || '',
      sambalKramank: student.sambalKramank || '',
    };
  };

  // Helper function to convert ApplicationFormData to ApplicationStudentDTO for update
  const convertApplicationFormToStudentDTO = (formData: ApplicationFormData): ApplicationStudentDTO => {
    // Extract class name from selectedClass (e.g., "4th-A" -> "4th")
    const getClassNameFromSelected = (selectedClass: any, admissionClass: string): string => {
      if (selectedClass?.name) {
        // If selectedClass name contains "-", take the part before it
        const classNamePart = selectedClass.name.split('-')[0];
        return classNamePart || selectedClass.name;
      }
      // Fallback to admissionClass if selectedClass is not available
      return admissionClass || '';
    };

    // Helper function to convert subject code back to full subject name
    // Form contains codes like "411", backend expects "(411) English"
    const convertSubjectCodeToFullName = (subjectCode: string, subjectType: 'language' | 'maths' | 'science'): string => {
      if (!subjectCode) return '';
      
      // Define subject mappings
      const languageSubjects: { [key: string]: string } = {
        '401': '(401) Hindi',
        '411': '(411) English',
        '512': '(512) Sanskrit',
        '508': '(508) Urdu',
        '502': '(502) Marathi',
        '504': '(504) Gujarati',
        '507': '(507) Punjabi',
        '509': '(509) Sindhi',
        '878': '(878) Electronics & Hardware'
      };
      
      const mathsSubjects: { [key: string]: string } = {
        '100': '(100) Maths (Standard)',
        '101': '(101) Maths (Basic)',
        '162': '(162) Painting',
        '163': '(163) Gayan Vadan',
        '165': '(165) Computer'
      };
      
      const scienceSubjects: { [key: string]: string } = {
        '200': '(200) Science',
        '162': '(162) Painting',
        '163': '(163) Gayan Vadan',
        '165': '(165) Computer'
      };
      
      let fullName = '';
      switch (subjectType) {
        case 'language':
          fullName = languageSubjects[subjectCode] || subjectCode;
          break;
        case 'maths':
          fullName = mathsSubjects[subjectCode] || subjectCode;
          break;
        case 'science':
          fullName = scienceSubjects[subjectCode] || subjectCode;
          break;
      }
      
      return fullName;
    };

    // Ensure studentName is not empty - this is required by backend
    const studentName = formData.studentName?.trim() || 'Unknown Student';
    if (!studentName) {
      throw new Error('Student name is required');
    }

    const normalizedAdmissionClass = (formData.admissionClass || selectedClass?.name || '').toString().trim().toLowerCase();
    const normalizedSection = (formData.section || '').toString().trim().toLowerCase();
    const allClasses = classesQuery.data || [];
    const matchedClass = allClasses.find((cls: any) =>
      (cls?.name || '').toString().trim().toLowerCase() === normalizedAdmissionClass &&
      (cls?.section || '').toString().trim().toLowerCase() === normalizedSection
    );
    const resolvedSchoolClassId =
      matchedClass?.id ||
      selectedClass?.id ||
      currentStudent?.schoolClassId ||
      1;

    return {
      applicationNo: formData.applicationNo || '',
      rollNumber: formData.rollNo || '',
      enrolmentNo: formData.enrolmentNo || '',
      studentCode: formData.studentCode || '', // Include studentCode field
      studentName: studentName, // This is the required field
      fatherName: formData.fatherName || '',
      motherName: formData.motherName || '',
      dateOfBirth: ensureValidDate(formData.dateOfBirth),
      gender: formData.gender || '',
      medium: formData.medium || 'Hindi',
      caste: formData.caste || '',
      subCaste: formData.subCaste || '',
      casteCertificate: formData.casteCertificate || '',
      religion: formData.religion || '',
      sssmid: formData.sssmid || '',
      scholarNo: formData.scholarNo || '',
      penNo: formData.penNo || '',
      apparId: formData.apparId || '',
      aadharNo: formData.aadharNo || '',
      houseNo: formData.houseNo || '',
      street: formData.street || '',
      landmark: formData.landmark || '',
      area: formData.area || '',
      city: formData.city || '',
      postOffice: formData.postOffice || '',
      district: formData.district || '',
      state: formData.state || '',
      pinCode: formData.pinCode || '',
      mobileNo: formData.mobileNo || '',
      bankName: formData.bankName || '',
      ifcCode: formData.ifcCode || '',
      accountNo: formData.accountNo || '',
      annualIncome: formData.annualIncome || '',
      section: formData.section || '',
      subjects: (() => {
        if (Array.isArray(formData.subjects)) {
          return formData.subjects;
        }
        if (typeof formData.subjects === 'string') {
          return (formData.subjects as string).split(',').filter(Boolean);
        }
        return [];
      })(), // Ensure subjects is always an array
      gmail: formData.gmail || '',
      admissionDate: ensureValidDate(formData.admissionDate),
      admissionClass: formData.admissionClass || '',
      admissionType: formData.admissionType || '',
      stream: formData.stream || '', // Stream field with default empty string
      schoolId: formData.schoolId || schoolId || 0,
      schoolClassId: resolvedSchoolClassId,
      sessionName: selectedSession?.name || '',
      sessionId: formData.sessionId || selectedSession?.id || 1,
      className: getClassNameFromSelected(selectedClass, formData.admissionClass),
      // Include photo and signature File objects if they exist (don't convert to empty string)
      studentPhoto: formData.studentPhoto || undefined,
      studentSignature: formData.studentSignature || undefined,
      // Add userId if available from the current student 
      ...(currentStudent?.userId ? { userId: currentStudent.userId } : {}),
      cwsnCode: formData.cwsnCode || '',
      sambalKramank: formData.sambalKramank || '',
      status: true // Add required status field
    };
  };

  // Helper function to convert ApplicationStudentDTO to StudentFormData for the update mutation
  const convertDTOToStudentFormData = (dto: ApplicationStudentDTO): any => {
    return {
      firstName: dto.studentName.split(' ')[0] || '',
      lastName: dto.studentName.split(' ').slice(1).join(' ') || '',
      rollNumber: dto.rollNumber,
      dateOfBirth: dto.dateOfBirth,
      gender: dto.gender,
      phone: dto.mobileNo,
      email: dto.gmail,
      guardianName: dto.fatherName, // Using father name as guardian
      guardianPhone: dto.mobileNo,
      guardianEmail: dto.gmail,
      className: dto.admissionClass,
      section: dto.section,
      schoolId: dto.schoolId,
      active: true,
      applicationNo: dto.applicationNo,
      enrolmentNo: dto.enrolmentNo,
      fatherName: dto.fatherName,
      motherName: dto.motherName,
      caste: dto.caste,
      subCaste: dto.subCaste,
      casteCertificate: dto.casteCertificate,
      religion: dto.religion,
      sssmid: dto.sssmid,
      scholarNo: dto.scholarNo,
      penNo: dto.penNo,
      apparIdNo: dto.apparId,
      aadharNo: dto.aadharNo,
      houseNo: dto.houseNo,
      street: dto.street,
      landmark: dto.landmark,
      area: dto.area,
      village: dto.city,
      postOffice: dto.postOffice,
      district: dto.district,
      state: dto.state,
      pinCode: dto.pinCode,
      bankName: dto.bankName,
      ifcCode: dto.ifcCode,
      accountNo: dto.accountNo,
      annualIncome: dto.annualIncome,
      subjects: dto.subjects || [], // Use the new subjects array directly
      gmail: dto.gmail,
      admissionDate: dto.admissionDate,
      admissionClass: dto.admissionClass,
      password: '', // Keep existing password
      medium: dto.medium,
      sessionId: dto.sessionId,
      classId: selectedClass?.id || 0,
    };
  };

  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentStudent(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (student: any) => {
    
    // Safely invalidate photo queries - wrap in try-catch to prevent logout on errors
    // These queries might fail with 404 if student doesn't have a photo, which is normal
    try {
      // Use removeQueries instead of invalidateQueries to avoid immediate refetch
      // The modal will fetch fresh data when it opens if needed
      queryClient.removeQueries({
        queryKey: ["studentPhotoInfo", student.id],
        exact: true
      });
      queryClient.removeQueries({
        queryKey: ["applicationPhotoInfo", student.id],
        exact: true
      });
    } catch (error) {
      // Silently handle any errors during query removal
      // This prevents logout if there's an issue with query cache
    }
    
    // Convert student to ApplicationFormData
    const convertedData = convertStudentToApplicationForm(student);
    
    // Check for empty fields
    const emptyFields = Object.entries(convertedData)
      .filter(([key, value]) => value === '' || value === null || value === undefined)
      .map(([key]) => key);
    
    
    setIsEdit(true);
    setCurrentStudent(student);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentStudent(undefined);
    setIsEdit(false);
  };

  // Create a Set of student IDs that have marksheets
  const studentsWithMarksheets = useMemo(() => {
    const marksheets = marksheetsQuery.data || [];
    return new Set(marksheets.map((marksheet: any) => marksheet.studentId || marksheet.student?.id).filter(Boolean));
  }, [marksheetsQuery.data]);

  // Check if a student has marksheets
  const hasMarksheet = (studentId: number): boolean => {
    return studentsWithMarksheets.has(studentId);
  };

  const handleOpenDeleteDialog = (id: number) => {
    // Check if student has marksheets before opening delete dialog
    if (hasMarksheet(id)) {
      showError(t('cannot_delete_student_with_marksheet'));
      return;
    }
    setStudentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (studentToDelete !== null) {
      try {
        await deleteStudentMutation.mutateAsync(studentToDelete);
        
        // Manually update the React Query cache to hide the deleted student immediately
        // This ensures the UI updates without waiting for a refetch
        if (studentsData && selectedClass?.id && selectedSession?.id) {
          // Find the student and set their status to false
          const updatedStudents = studentsData.map((student: any) => 
            student.id === studentToDelete 
              ? { ...student, status: false }
              : student
          );
          
          // Update the specific query cache that this component is using
          const queryKey = ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id];
          queryClient.setQueryData(queryKey, updatedStudents);
          
          // Also update the local state to ensure immediate re-render
          // This is a backup approach in case the cache update doesn't trigger immediately
        }
        
        // Close the dialog after successful deletion
        handleCloseDeleteDialog();
        // Show success message in top right corner snackbar
        showSuccess(t('student_deleted_successfully'));
      } catch (error: any) {
        // Show error message in top right corner snackbar
        showError(error?.message || t('failed_to_delete_student'));
      }
    }
  };

  const handleOpenHistoryModal = (student: any) => {
    setHistoryStudentId(student.id);
    setHistoryStudentName(`${student.firstName || ''} ${student.lastName || ''}`.trim());
    setHistoryStudentRollNo(student.rollNumber || '');
    setHistoryModalOpen(true);
  };
  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setHistoryStudentId(null);
    setHistoryStudentName('');
    setHistoryStudentRollNo('');
  };

  // Download students handler - opens sort modal
  const handleDownloadStudents = () => {
    if (!selectedClass || !selectedSession || !schoolId) {
      showError(t('please_select_class_session_and_school'));
      return;
    }
    setSortModalOpen(true);
  };

  // Handle sort selection and download
  const handleSortSelection = (sortBy: "rollnumber" | "studentname") => {
    if (!selectedClass || !selectedSession || !schoolId) {
      showError(t('please_select_class_session_and_school'));
      return;
    }

    const downloadParams = {
      schoolId: schoolId,
      classId: selectedClass.id!,
      sessionId: selectedSession.id!,
      sortBy: sortBy
    };

    downloadStudentsMutation.mutate(downloadParams, {
      onSuccess: () => {
        showSuccess(t('students_downloaded_successfully'));
        setSortModalOpen(false);
      },
      onError: (error: any) => {
        showError(t('download_data_sheet_failed').replace('{error}', error?.message || 'Unknown error'));
      }
    });
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setMenuRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRow(null);
  };

  const handleOpenProfileStatusDialog = (student: any) => {
    // Read userStatus from fresh query data (studentsData) to get latest status from API response
    const studentFromQuery = studentsData.find((s: any) => s.id === student.id) as any;
    // Use userStatus from query data if available, otherwise fallback to student parameter
    const userStatus = studentFromQuery?.userStatus !== undefined 
      ? studentFromQuery.userStatus 
      : ((student as any)?.userStatus !== undefined ? (student as any).userStatus : true);
    // true = enabled, false = disabled
    setStudentToUpdateStatus({ id: student.id, userStatus: userStatus === true });
    setProfileStatusDialogOpen(true);
  };

  const handleCloseProfileStatusDialog = () => {
    setProfileStatusDialogOpen(false);
    setStudentToUpdateStatus(null);
  };

  const handleConfirmProfileStatusUpdate = async () => {
    if (studentToUpdateStatus !== null) {
      try {
        const response = await updateStudentUserStatusMutation.mutateAsync(studentToUpdateStatus.id);
        // Backend returns UserStatusDto: { userId, studentId, status, message }
        
        // Extract message and status from response (handle both string and object responses)
        let message: string;
        let newUserStatus: boolean | undefined;
        if (typeof response === 'string') {
          message = response;
        } else if (response && typeof response === 'object') {
          const responseObj = response as any;
          message = responseObj?.message || responseObj?.data || JSON.stringify(response);
          // Read userStatus (or status) from response (UserStatusDto)
          if (typeof responseObj.userStatus === 'boolean') {
            newUserStatus = responseObj.userStatus;
          } else if (typeof responseObj.status === 'boolean') {
            newUserStatus = responseObj.status;
          }
        } else {
          message = String(response);
        }
        
        // Update the query cache with the new userStatus from response
        if (newUserStatus !== undefined && selectedSession?.id && selectedClass?.id) {
          const queryKey = ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id];
          queryClient.setQueryData(queryKey, (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((student: any) =>
              student.id === studentToUpdateStatus.id
                ? { ...student, userStatus: newUserStatus }
                : student
            );
          });
        }
        
        // Display the exact message from backend
        showSuccess(message || t('student_profile_status_updated') || 'Student profile status updated');
        handleCloseProfileStatusDialog();
        handleMenuClose();
        // Refetch students to update the UI (as fallback if cache update fails)
        refetch();
      } catch (error: any) {
        showError(error?.message || t('failed_to_update_student_profile_status') || 'Failed to update student profile status');
      }
    }
  };

  const handleApplicationFormSubmit = (formData: ApplicationFormData) => {
    
    if (isEdit && currentStudent?.id) {
      try {
        // Validate required fields first
        if (!formData.studentName?.trim()) {
          showError(t('student_name_required'));
          return;
        }
        
        if (!formData.fatherName?.trim()) {
          showError(t('father_name_required'));
          return;
        }
        
        if (!formData.motherName?.trim()) {
          showError(t('mother_name_required'));
          return;
        }
        
        if (!formData.dateOfBirth?.trim()) {
          showError(t('date_of_birth_required'));
          return;
        }
        
        if (!formData.gender?.trim()) {
          showError(t('gender_required'));
          return;
        }
        
        // Convert ApplicationFormData to ApplicationStudentDTO
        const studentDTO = convertApplicationFormToStudentDTO(formData);
        
        // Use the DTO directly with the new mutation
        
        updateStudentWithDTOMutation.mutate(
          { id: currentStudent.id, data: studentDTO },
          {
            onSuccess: () => {
              // Comprehensive query invalidation after student update
              // Invalidate student class mapping queries
              queryClient.invalidateQueries({
                queryKey: ["studentClassMapping"]
              });
              queryClient.invalidateQueries({
                queryKey: ["studentClassMapping", "class", selectedClass?.id, "session", selectedSession?.id]
              });
              
              // Invalidate all student queries
              queryClient.invalidateQueries({
                queryKey: ["students"]
              });
              queryClient.invalidateQueries({
                queryKey: ["students", schoolId]
              });
              
              // Invalidate students by sessionId and classId query to refresh the list
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.invalidateQueries({ 
                  queryKey: ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id] 
                });
              }
              
              // Invalidate current student query (for student profile)
              queryClient.invalidateQueries({
                queryKey: ["currentStudent", currentStudent.id]
              });
              
              // Invalidate photo-related queries
              queryClient.invalidateQueries({
                queryKey: ["studentPhotoInfo", currentStudent.id]
              });
              queryClient.invalidateQueries({
                queryKey: ["applicationPhotoInfo", currentStudent.id]
              });
              
              // Invalidate application forms queries (in case subjects changed)
              queryClient.invalidateQueries({
                queryKey: ["applicationForms"]
              });
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.invalidateQueries({ 
                  queryKey: ["applicationFormsBySessionIdAndClassId", selectedSession.id, selectedClass.id] 
                });
              }
              if (schoolId) {
                queryClient.invalidateQueries({
                  queryKey: ["applicationFormsBySchoolId", schoolId]
                });
              }
              
              // Invalidate subjects-related queries (in case subjects changed)
              queryClient.invalidateQueries({
                queryKey: ["subjects"]
              });
              
              // Refetch students list to ensure UI is updated
              refetch();
              if (selectedSession?.id && selectedClass?.id) {
                queryClient.refetchQueries({
                  queryKey: ["studentsBySessionIdAndClassId", selectedSession.id, selectedClass.id],
                  exact: true,
                });
              }
              
              setModalOpen(false);
              showSuccess(t('student_updated_successfully'));
            },
            onError: (error: any) => {
              // Extract error message from backend response
              const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || t("failed_to_update_student");
              showError(errorMessage);
            }
          }
        );
      } catch (error) {
        showError(t('error_processing_form_data'));
      }
    }
  };

  const columns = [
    { id: 'studentName', label: t('student_name'), minWidth: 200 },
    { id: 'rollNumber', label: t('roll_number'), minWidth: 100 },
    { id: 'mobileNo', label: t('mobile_number'), minWidth: 100 },
    { id: 'className', label: t('class'), minWidth: 100 },
    { id: 'section', label: t('section'), minWidth: 100 },
  ];

  let infoMessage = '';
  if (!selectedClass && !selectedSession) infoMessage = t('please_select_class_and_session');
  else if (!selectedClass) infoMessage = t('please_select_class');
  else if (!selectedSession) infoMessage = t('please_select_session');

  // Filter students to only show those with status: true, from current school, and matching selected medium
  // Use the new API data instead of the old one
  // Note: API already filters by sessionId and classId, so we only need to filter by status, schoolId, and medium
  const studentsData = studentsQuery.data || [];
  const filteredStudents = (studentsData || [])
    .filter((student: any) => {
      // Basic filters - only filter by status and schoolId
      // Note: sessionId and classId filtering is already done by the API
      if (student.status !== true) {
        return false;
      }
      
      // Optional: Filter by schoolId if needed (but API should already handle this)
      if (schoolId && student.schoolId !== schoolId) {
        return false;
      }

      // Keep the table aligned with selected class section (e.g. 9th-A shows only A).
      if (selectedClass?.section && selectedClass.section !== "__GRADE_ALL__") {
        const studentSection = (student.section || "").toString().trim().toLowerCase();
        const selectedSection = selectedClass.section.toString().trim().toLowerCase();
        if (studentSection !== selectedSection) {
          return false;
        }
      }
      
      // Medium filter - only filter if medium is explicitly set and not "ALL"
      if (selectedMedium && selectedMedium.trim() !== "" && selectedMedium !== "ALL") {
        const studentMedium = student.medium;
        if (!studentMedium) {
          // If student has no medium, include it (don't exclude)
          return true;
        }
        if (typeof studentMedium === 'string') {
          // If medium is a string, check if it contains the selected medium
          if (!studentMedium.toLowerCase().includes(selectedMedium.toLowerCase())) {
            return false;
          }
        } else if (Array.isArray(studentMedium)) {
          // If medium is an array, check if it includes the selected medium
          if (!studentMedium.some((m: string) => m.toLowerCase() === selectedMedium.toLowerCase())) {
            return false;
          }
        } else {
          // Unknown format, include it
          return true;
        }
      }
      
      return true;
    })
    .sort((a: any, b: any) => {
      // Sort by student name alphabetically (A-Z)
      const nameA = (a.studentName || `${a.firstName || ''} ${a.lastName || ''}`.trim()).toLowerCase();
      const nameB = (b.studentName || `${b.firstName || ''} ${b.lastName || ''}`.trim()).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  
  const formattedData = filteredStudents.map((row: any, index: number) => ({
    ...row,
    studentName: row.studentName || `${row.firstName || ''} ${row.lastName || ''}`.trim(),
    mobileNo: row.mobileNo || row.phone || '', // Normalize mobile number for search
    phone: row.phone || row.mobileNo || '', // Include phone as well for search
  }));

  const renderActions = (row: any) => (
    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
      <MoreVertIcon />
    </IconButton>
  );

  if (tenantLoading || !schoolId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Paper sx={{ p: 2, maxWidth: 1500, mx: 'auto', mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            {t('student_management')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(isSchoolAdmin() || isTeacher()) && (
              <Button 
                variant="outlined" 
                sx={{ 
                  borderColor: "#3092e3", 
                  color: "#3092e3",
                  "&:hover": { 
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(48, 146, 227, 0.04)",
                  } 
                }}
                onClick={() => setScholarNumberModalOpen(true)}
                startIcon={<AddIcon />}
              >
                {t('add_scholar_number') || 'Add Scholar Number'}
              </Button>
            )}
            <Button 
              variant="contained" 
              sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
              onClick={handleDownloadStudents}
              disabled={downloadStudentsMutation.isLoading || !selectedClass || !selectedSession || !schoolId}
              startIcon={downloadStudentsMutation.isLoading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
            >
              {downloadStudentsMutation.isLoading ? t('downloading') : t('data_sheet')}
            </Button>
          </Box>
        </Box>
        {infoMessage ? (
          <Alert severity="info" sx={{ mb: 2 }}>{infoMessage}</Alert>
        ) : isLoading ? (
          <CircularProgress sx={{ display: 'block', margin: '20px auto' }} />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error.message}</Alert>
        ) : (
          <EnhancedTable
            columns={columns}
            data={formattedData}
            searchFields={['studentName', 'rollNumber', 'className', 'section', 'mobileNo', 'phone']}
            filterFields={[]}
            cardProps={{
              elevation: 0,
              sx: { 
                border: 'none',
                boxShadow: 'none',
                backgroundColor: 'transparent'
              }
            }}
            actions={renderActions}
          />
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              if (menuRow) handleOpenEditModal(menuRow);
              handleMenuClose();
            }}
          >
            {t('edit')}
          </MenuItem>
          <Tooltip
            title={menuRow && hasMarksheet(menuRow.id) ? t('cannot_delete_student_with_marksheet_tooltip') : ''}
            placement="left"
            arrow
          >
            <span>
              <MenuItem
                onClick={() => {
                  if (menuRow) handleOpenDeleteDialog(menuRow.id);
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
                disabled={deleteStudentMutation.isLoading || (menuRow && hasMarksheet(menuRow.id))}
              >
                {deleteStudentMutation.isLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {t('deleting')}
                  </>
                ) : (
                  t('delete')
                )}
              </MenuItem>
            </span>
          </Tooltip>
          <MenuItem
            onClick={() => {
              if (menuRow) handleOpenHistoryModal(menuRow);
              handleMenuClose();
            }}
          >
            {t('view')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              if (menuRow) {
                handleOpenProfileStatusDialog(menuRow);
                handleMenuClose();
              }
            }}
            disabled={updateStudentUserStatusMutation.isLoading}
            sx={{ color: 'warning.main' }}
          >
            {updateStudentUserStatusMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('updating') || 'Updating...'}
              </>
            ) : (() => {
              // Read userStatus from fresh query data (studentsData) to get latest status from API response
              const studentFromQuery = studentsData.find((s: any) => s.id === menuRow?.id) as any;
              // Use userStatus from query data if available, otherwise fallback to menuRow
              const userStatus = studentFromQuery?.userStatus !== undefined 
                ? studentFromQuery.userStatus 
                : ((menuRow as any)?.userStatus !== undefined ? (menuRow as any).userStatus : true);
              // true = enabled (show "Disable Student"), false = disabled (show "Enable Student")
              return userStatus === true
                ? t('disable_student') || 'Disable Student'
                : t('enable_student') || 'Enable Student';
            })()}
          </MenuItem>
        </Menu>
      </Paper>

      {/* ApplicationFormModal for editing students */}
      <ApplicationFormModal
        open={modalOpen}
        onClose={handleCloseModal}
        initialData={currentStudent ? convertStudentToApplicationForm(currentStudent) : undefined}
        onSubmit={handleApplicationFormSubmit}
        isEdit={isEdit}
        isStudentEdit={true} // This indicates it's being used for student editing
        schoolData={{
          nameofSchool: school?.nameofSchool || '',
          blockName: school?.blockName || '',
          district: school?.district || '',
          diseCode: school?.diseCode || '',
          state: school?.state || '',
          pinNo: school?.pinNo || '',
          schoolCode: school?.schoolCode || '',
          schoolLogo: school?.schoolLogo || '',
          medium: (() => {
            const schoolMedium = school?.medium;
            return Array.isArray(schoolMedium) 
              ? schoolMedium.join(', ') 
              : schoolMedium || '';
          })(),
        }}
        selectedClass={selectedClass}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('confirm_delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('are_you_sure_you_want_to_delete_this_student')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}>
            {t('cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            autoFocus
            disabled={deleteStudentMutation.isLoading}
          >
            {deleteStudentMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('deleting')}
              </>
            ) : (
              t('delete')
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={profileStatusDialogOpen}
        onClose={handleCloseProfileStatusDialog}
        aria-labelledby="profile-status-dialog-title"
        aria-describedby="profile-status-dialog-description"
      >
        <DialogTitle id="profile-status-dialog-title">
          {studentToUpdateStatus?.userStatus
            ? t("confirm_disable_profile") || "Confirm Disable Profile"
            : t("confirm_enable_profile") || "Confirm Enable Profile"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="profile-status-dialog-description">
            {studentToUpdateStatus?.userStatus
              ? t("are_you_sure_you_want_to_disable_student_profile_and_login") || "Are you sure you want to disable student profile and login?"
              : t("are_you_sure_you_want_to_enable_student_profile_and_login") || "Are you sure you want to enable student profile and login?"}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileStatusDialog} sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" }, color: "white" }}>
            {t("cancel")}
          </Button>
          <Button
            onClick={handleConfirmProfileStatusUpdate}
            color={studentToUpdateStatus?.userStatus ? "warning" : "success"}
            autoFocus
            disabled={updateStudentUserStatusMutation.isLoading}
          >
            {updateStudentUserStatusMutation.isLoading ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                {t('updating') || 'Updating...'}
              </>
            ) : (
              studentToUpdateStatus?.userStatus
                ? t("disable_login") || "Disable Login"
                : t("enable_login") || "Enable Login"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <StudentHistoryModal
        open={historyModalOpen}
        handleClose={handleCloseHistoryModal}
        studentId={historyStudentId}
        studentName={historyStudentName}
        rollNo={historyStudentRollNo}
      />

      <StudentDataSheetSortModal
        open={sortModalOpen}
        onClose={() => setSortModalOpen(false)}
        onSelectSortBy={handleSortSelection}
        isLoading={downloadStudentsMutation.isLoading}
      />

      <ScholarNumberModal
        open={scholarNumberModalOpen}
        handleClose={() => setScholarNumberModalOpen(false)}
        onSubmit={async (students: StudentRollScholarUpdateDto[]) => {
          try {
            await updateRollNumberAndScholarNumberMutation.mutateAsync(students);
            showSuccess(t("scholar_numbers_updated_successfully") || "Scholar numbers updated successfully");
            setScholarNumberModalOpen(false);
            refetch();
          } catch (error: any) {
            showError(
              error?.response?.data?.message ||
                t("error_updating_scholar_numbers") ||
                "Error updating scholar numbers"
            );
          }
        }}
      />
    </>
  );
};

export default StudentManagement;